import { usePredictorStore } from './predictorStore';
import { useQuery } from '@tanstack/react-query';
import { isSuccess } from '../../common/serviceUtil';
import { getPredictors, getPredictorStats } from '../../services/predictorService';
import { findPredictorsToUse } from './predictor.functions';
import { useEventStore } from '../event/eventStore';
import { Predictor } from '../../interfaces/predictors';

import { useEntryStore } from '../entry/entryStore';
import { secsDivisor } from '../../common/dateUtils';
import { useConfigStore } from '../options/optionsStore';
import config from '../../brands/bet365/config';

/**
 * @param predictor
 */
const getEventMap = (
  predictor?: Predictor,
): [
  number,
  {
    eventId: number;
  },
][] => predictor?.events?.map((event) => [event.markets[0].eventNum, { eventId: event.eventId }]) ?? [];
const url = new URL(window.location.href);

const countryCodeParam = url.searchParams.get(config.settings.countryCodeIdentifier);

export const useGetActivePredictor = (predictorId?: number) => {
  const setActivePredictor = usePredictorStore((s) => s.setActivePredictor);
  const setNextPredictor = usePredictorStore((s) => s.setNextPredictor);
  const setEventNumberMap = useEventStore((s) => s.setEventNumberMap);
  const CountryCode = useConfigStore((s) => s.countryCode);

  return useQuery(
    ['getPredictors', predictorId],
    ({ signal }) => getPredictors(signal, countryCodeParam || CountryCode),
    {
      refetchOnWindowFocus: false,
      onSettled: (data, error) => {
        if (isSuccess(data)) {
          const { active, next } = findPredictorsToUse(data.value, predictorId);
          setActivePredictor(active);
          setEventNumberMap(new Map(getEventMap(active)));
          setNextPredictor(next);

          return;
        }
      },
    },
  );
};

export const useGetPredictorStats = (predictorId?: number) => {
  const picks = useEntryStore((s) => s.entry?.picks);
  const predictorStat = usePredictorStore((s) => s.predictorStats);
  const numResultedPicks = picks?.filter((pick) => pick.resulted).length;
  const outOfSync = picks?.filter((pick) => pick.resulted).length !== predictorStat?.predictorEventsComplete;
  const setPredictorStats = usePredictorStore((s) => s.setPredictorStats);
  return useQuery(
    ['getPredictorStats', predictorId, numResultedPicks],
    ({ signal }) =>
      getPredictorStats({
        predictorId,
        signal,
      }),
    {
      onSettled: (data, error) => {
        if (isSuccess(data)) {
          setPredictorStats(data.value);
          return;
        }
      },
      enabled: outOfSync,
      refetchOnWindowFocus: false,
      staleTime: 30 * secsDivisor,
    },
  );
};
